import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import FilterDropDown from './DropDown';
import DateRangePickerComponent from './DateRangePicker/DateRangePickerComponent';
import NestedTable from './NestedTable'; // Import the NestedTable component
import appConfig from "../config/appConfig";
import { useTranslation } from 'react-i18next';

const DefaultTable = ({
  headers,
  data = [],
  accessor = [],
  rightHeaderIndex,
  renderCustomCells,
  renderCells,
  handlePagination,
  currentLimit,
  currentPage,
  filterAction,
  isFilterEnabled,
  isDateFilter = false,
  dateFilterLabel,
  selectedDateObj,
  totalCount,
  isSearchEnabled,
  searchPlaceholderText,
  filterOptions,
  defaultFilter,
  selected = null,
  loadRowDetails = null,
  enableRowDetails = false,
  searchCallback,
  searchKey,
  isExportEnabled,
  exportCallback,
  isRefreshEnabled = false,
  refreshCallback,
  isSortEnabled,
  handleSort,
  sortValue,
  isFilterOpen,
  nestedData = [],
}) => {
  const { t } = useTranslation('common');

  const [page, setPage] = useState(currentPage);
  const [rowsPerPage, setRowsPerPage] = useState(currentLimit);
  const [selectedFilter, setSelectedFilter] = useState(defaultFilter);
  const [searchText, setSearchText] = useState(searchKey);
  const [orderBy, setOrderBy] = useState({ order: sortValue.sortOrder, field: sortValue.sortBy });
  const searchRef = useRef();

  const handleDummy = (e, nextPage) => {
    handlePagination(rowsPerPage, nextPage);
    setPage(nextPage);
  };

  const onLimitChange = (limit) => {
    setRowsPerPage(limit.value);
    handlePagination(limit.value, 1);
  };

  const checkKeyPress = useCallback(
    (e) => {
      const { key } = e;
      if (key === 'Enter') {
        searchCallback(searchText.trim());
      }
    },
    [searchText]
  );

  useEffect(() => {
    if (searchRef.current) searchRef.current.addEventListener('keypress', checkKeyPress);
  }, [searchText]);

  const handleSorting = (header) => {
    if (header.sortEnabled) {
      setOrderBy({
        order: orderBy.order === 'asc' ? 'desc' : 'asc',
        field: header.name,
      });
      const sortObj = {
        sortBy: header.sortBy,
        sortOrder: orderBy.order === 'asc' ? 'desc' : 'asc',
      };
      handleSort(sortObj);
      handlePagination(rowsPerPage, 1);
    }
  };

  const renderHeaders = headers?.map((header, index) => (
    <TableCell
      sx={{ fontFamily: appConfig.fontfamily, fontSize: '16px', textAlign: rightHeaderIndex?.includes(index) ? 'right !important' : 'left' }}
      key={header.name}
    >
      <b
        style={{
          cursor: isSortEnabled && header.sortEnabled ? 'pointer' : 'default',
          whiteSpace: 'nowrap',
        }}
        onClick={() => {
          handleSorting(header);
        }}
      >
        {header.name}
      </b>
      {isSortEnabled && header.sortEnabled ? (
        <IconButton
          onClick={() => {
            handleSorting(header);
          }}
          className="pad-0"
          title={`${orderBy.order === 'desc' ? 'descending' : 'ascending'}`}
          aria-label="sort by"
          size="small"
        >
          {orderBy.order === 'desc' ? (
            <ArrowDownwardIcon sx={{ fontSize: '17px' }} />
          ) : (
            <ArrowUpwardIcon sx={{ fontSize: '17px' }} />
          )}
        </IconButton>
      ) : header.sortEnabled ? (
        <SortIcon sx={{ fontSize: '17px', verticalAlign: 'middle', marginLeft: '5px' }} />
      ) : null}
    </TableCell>
  ));

  const renderTableBody = data?.length === 0 ? (
    <TableRow>
      <TableCell key={0} sx={{ fontFamily: appConfig.fontfamily, fontSize: '16px' }}>
        No Data
      </TableCell>
    </TableRow>
  ) : (
    data?.map((row) => {
      const accessKey = accessor?.map((key) => key);
      return (
        <>
          <TableRow
            key={row.id}
            sx={{
              background: '#fafafa',
              ' &.Mui-selected': {
                background: selected === row.id ? '#fafafa' : '',
                '&:hover': {
                  background: '#fafafa',
                },
              },
            }}
            selected={selected === row.id}
          >
            {renderCustomCells ? (
              renderCells(row)
            ) : (
              <>
                {accessKey?.map((dataKey) => (
                  <TableCell
                    sx={{ fontFamily: appConfig.fontfamily, fontSize: '16px', verticalAlign: 'top' }}
                    key={dataKey}
                  >
                    {row[dataKey] === null ? '-' : row[dataKey]}
                  </TableCell>
                ))}
                {/* Render nested table in the third column */}
                <TableCell key="nested-table">
                  <NestedTable changeLog={row.changeLog} />
                </TableCell>
              </>
            )}
          </TableRow>
          {selected === row.id && enableRowDetails && (
            <TableRow>
              <TableCell colSpan={headers.length + 2}>
                <Accordion>
                  <AccordionDetails sx={{ border: '1px solid #ccc' }}>
                    {loadRowDetails}
                  </AccordionDetails>
                </Accordion>
              </TableCell>
            </TableRow>
          )}
        </>
      );
    })
  );

  return (
    <Grid container spacing={2}>
      <Grid container>
        <Grid item xs={12} sm={12} xl={3.5} lg={3.5} md={12}>
          {totalCount > 0 ?
            <div className='inline-blk' style={{ color: '#757575', paddingLeft: "20px", paddingTop: isFilterOpen ? 60 : 0 }}>
              <p>{`${t("Viewing")}: ${page > 1 ? (page - 1) * rowsPerPage + 1 : page} - ${(page * rowsPerPage) > totalCount ? totalCount : page * rowsPerPage} of ${totalCount || 0} ${t("records")}`}</p>
            </div> : null}
          {totalCount > 0 && isRefreshEnabled ? <IconButton onClick={refreshCallback} sx={{ background: appConfig.colors.grey, marginLeft: "20px" }} aria-label="refresh">
            <RefreshIcon />
          </IconButton> : null}
        </Grid>
      </Grid>
      <Grid item xs={12} sm={12} xl={12} lg={12} md={12} style={{ paddingTop: 0 }}>
        {totalCount > 0 && <div style={{ border: "1px solid #e5e5e5", height: "1px", width: "100%", marginTop: "0px" }} />}
      </Grid>

      <Grid item xs={12} sm={12} xl={12} lg={12} md={12}>
        <div style={{
          overflowY: "auto",
        }}>
          {data?.length !== 0 ? <TableContainer component={Paper}>
          <Table>
        <TableHead>
          <TableRow>
            {renderHeaders}
          </TableRow>
        </TableHead>
        <TableBody>
          {renderTableBody}
        </TableBody>
      </Table>
          </TableContainer> : <div style={{ color: appConfig.colors.darkgray, textAlign: "center", marginTop: "8%" }}> <FormatListBulletedIcon sx={{ fontSize: 80, fontFamily: appConfig.fontfamily }} /><h2 style={{ fontSize: appConfig.font16 }}>No Data</h2></div>}
        </div>
        <br />
    {data?.length !== 0 && currentLimit && (
          <div style={{ float: "right", display: "flex" }}>
            <div style={{ marginRight: "10px" }}>
              <FilterDropDown isEnableFirstItem={false} selectedValue={{ key: currentLimit, value: currentLimit }} selectOptions={[{ key: currentLimit, value: currentLimit }, { key: '10', value: '10' }, { key: '50', value: '50' }, { key: '100', value: '100' }]} onSelect={onLimitChange} />
            </div>
            <Pagination page={page} onChange={handleDummy} count={Math.ceil(totalCount / rowsPerPage)} 
            
            sx={{
              "& .MuiPaginationItem-root": {
                color: appConfig.colors.darkblue,
              }
            
            }}
            
            variant="outlined" shape="rounded" />
          </div>
        )}
        </Grid>
    </Grid>
  );
};

DefaultTable.propTypes = {
  headers: PropTypes.array.isRequired,
  data: PropTypes.array,
  accessor: PropTypes.array,
  rightHeaderIndex: PropTypes.array,
  renderCustomCells: PropTypes.bool,
  renderCells: PropTypes.func,
  handlePagination: PropTypes.func,
  currentLimit: PropTypes.number,
  currentPage: PropTypes.number,
  filterAction: PropTypes.func,
  isFilterEnabled: PropTypes.bool,
  isDateFilter: PropTypes.bool,
  dateFilterLabel: PropTypes.string,
  selectedDateObj: PropTypes.object,
  totalCount: PropTypes.number,
  isSearchEnabled: PropTypes.bool,
  searchPlaceholderText: PropTypes.string,
  filterOptions: PropTypes.array,
  defaultFilter: PropTypes.object,
  selected: PropTypes.string,
  loadRowDetails: PropTypes.node,
  enableRowDetails: PropTypes.bool,
  searchCallback: PropTypes.func,
  searchKey: PropTypes.string,
  isExportEnabled: PropTypes.bool,
  exportCallback: PropTypes.func,
  isRefreshEnabled: PropTypes.bool,
  refreshCallback: PropTypes.func,
  isSortEnabled: PropTypes.bool,
  handleSort: PropTypes.func,
  sortValue: PropTypes.object,
};

export default DefaultTable;
